import React, { useContext, useRef, useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import Draggable from 'react-draggable';
import TextareaAutosize from 'react-textarea-autosize';
import ContentEditable from 'react-contenteditable';
import _ from 'lodash';

import EditorContext, { groupChoices } from '@mirinae/lessons/editor/modules/contexts/editor';
import GlobalContext, { SyncContext } from '@mirinae/lessons/modules/contexts/global';
// import { fontkoKR, fontDefault, classes, IconDots } from '@mirinae/react-ui';
// import { colorMap } from '@mirinae/lessons/modules/defines/styles';
import PopupTitle from '@mirinae/lessons/components/common/PopupTitle';
import { TextField, SmallButton, CheckboxField } from '@mirinae/lessons/editor/components/PropertySheets/UI';
import { Select } from '@mirinae/lessons/components/admin/LessonList';
import httpAPI from '@mirinae/lessons/modules/apis/http';
import { apiPaths } from '@mirinae/shared/defines/paths';
import PopupDialog from './PopupDialog';
import SelectGrammarPattern from './SelectGrammarPattern';
import SpecContextMenu from './SpecContextMenu';
import { elementComponents } from '../../../components/lesson/elementComponentMap';
import PatternAnimation, { computeAnimationLayout } from '../../../components/lesson/elements/PatternAnimation';

const { escapeRegExp } = _;

const LessonSpecBoxWrapper = styled.div`
    position: absolute;
    ${props => !props.expanded && 'max-width: 500px;'}
    font-family: Arial;
    overflow-x: hidden;
    width: ${props => (props.expanded ? 850 : 200)}px;
    top: 300px;
    left: 400px;
    z-index: 999;
    background: white;
    border-radius: 6px;
    box-shadow: 0 3px 1px -2px rgb(0 0 0 / 20%), 0 2px 2px 0 rgb(0 0 0 / 14%), 0 1px 5px 0 rgb(0 0 0 / 12%);
`;

const EditorTitleBar = styled(PopupTitle)`
    font-size: 12px;
    background: #dddcdc;
    height: 20px;
    margin: 0;
    padding: 6px;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    justify-content: space-between;
    
    span {
        font-family: Webdings;
        color: #868686;
        font-size: 16px;
        padding: 0 2px 3px 2px;
        cursor: pointer;
    }
`;

const ToolBar = styled.div`
    display: flex;
    flex-direction: row;
    align-content: center;
    justify-content: space-between;
    padding: 7px 6px 4px 12px;
    border-bottom: solid 1px lightgray;
`;

const Hints = styled.div`
    align-self: center;
    font-size: 10px;
    margin: 2px 0px 8px;
    white-space: pre;
`;

const Field = styled.tr`
    background: ${props => (props.highlight ? '#fff8f8' : 'none')};
`;

const Cell = styled.td`
    border: thin solid #efefef;
    font-size: 11px;
    vertical-align: middle;
    min-width: 40px;
    padding: 3px 4px;
    vertical-align: top;
`;

const Label = styled(Cell)`
    font-size: 12px;
    font-weight: bold;
    vertical-align: top;
`;

const SpecTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    border: thin solid #e0e0e0;
    margin: 6px;
`;

const SpecTableBody = styled.tbody`
    display: block;
    max-height: 800px;
    overflow-y: scroll;
`;

const ContentsCell = styled(Cell)`
    padding-left: 5px;
`;

const ContentsText = styled(TextareaAutosize)`
    font-size: 12px;
    overflow: hidden;
    display: block;
    width: 100%;
    &:focus {
        background: aliceblue;
    }
`;

const AnimSpecTable = styled(SpecTable)`
    margin: 0px;
    & + & {
        margin-top: 7px;
    }
`;

const AnimSpecTableBody = styled.tbody`
    padding: 0px;
    margin: 0px;
`;

const EditableLabel = styled(ContentEditable)`
    font-size: 12px;
    font-weight: bold;
    text-align: left;
    padding: 0px 5px;
    width: max-content;
    max-width: 100px;
    
    &:focus {
        background: aliceblue;
    }
`;

const Expander = styled.div`
    cursor: pointer;
`;

const SRWrapper = styled.div`
    position: relative;
    margin-right: 10px;
`;

const SRPopup = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: absolute;
    background: white;
    top: 30px; // ${props => props.top}px;
    left: 10px;
    border: thin solid lightgray;
    box-shadow: 0 3px 1px -2px rgb(0 0 0 / 20%), 0 2px 2px 0 rgb(0 0 0 / 14%), 0 1px 5px 0 rgb(0 0 0 / 12%);
    width: 400px;
    padding: 6px 15px;
    z-index: 1000;
    cursor: move;
`;

const SRPopupButttons = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

export const SearchAndReplace = ({ srObject, setSRObject, spec, updateSpec, onReplace }) => {
    const [haveIput, setHaveInput] = useState(false);
    const findRef = useRef(null);
    const replRef = useRef(null);
    const srwRef = useRef(null);
    const dragRef = useRef(null);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyUp);
        return () => document.removeEventListener('keydown', handleKeyUp);
    }, [srObject]);

    useEffect(() => {
        if (srObject && findRef.current)
            findRef.current.focus();
    }, [srObject]);

    const handleKeyUp = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
            e.preventDefault();
            e.stopPropagation();
            setSRObject(spec);
        } else if (srObject && e.key === 'Enter') {
            handleReplace();
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const handleReplace = () => {
        const deepRepl = (obj, from, to, index) => {
            const el = index !== undefined ? obj[index] : obj;
            if (typeof el === 'string')
                obj[index] = el.replace(from, to);
            else if (Array.isArray(el))
                el.map((e, i) => deepRepl(el, from, to, i));
            else if (typeof el === 'object')
                for (const prop in el)
                    deepRepl(el, from, to, prop);
        };
        deepRepl(srObject, new RegExp(escapeRegExp(findRef.current.value)), replRef.current.value);
        updateSpec();
        onReplace();
        setSRObject(null);
    };

    const where = () => (srObject === spec ? 'spec' : srObject.elements ? 'page' : 'animation');

    return (
        <SRWrapper ref={srwRef}>
            <SmallButton variant="outlined" onClick={() => setSRObject(spec)}>Search and Replace</SmallButton>
            { srObject && (
                <Draggable nodeRef={dragRef}
                    handle="#sr-popup" bounds="#root" axis="both" defaultPosition={{ x: 0, y: 0 }}>
                    <SRPopup ref={dragRef}>
                        <div id="sr-popup" style={{ height: 10, backgroundColor: 'lightGray', cursor: 'move' }} />
                        <TextField size="small" inputRef={findRef} onChange={() => setHaveInput(true)} label="Find" />
                        <TextField size="small" inputRef={replRef} onChange={() => setHaveInput(true)} label="Replace" />
                        <SRPopupButttons>
                            <SmallButton variant="outlined" onClick={() => setSRObject(null)}>Cancel</SmallButton>
                            <SmallButton variant="outlined" defaultBtn onClick={handleReplace} disabled={!haveIput}>Replace all in {where()}</SmallButton>
                        </SRPopupButttons>
                    </SRPopup>
                </Draggable>
            )}
        </SRWrapper>
    );
};

const LessonSpecBox = () => {
    const editor = useContext(EditorContext);
    const global = useContext(GlobalContext);
    const [expanded, setExpanded] = useState(true);
    const [animExpanded, setAnimExpanded] = useState({});
    const specBoxRef = useRef(null);
    const menuRef = useRef(null);
    const [srObject, setSRObject] = useState(null);
    const [showLoadPattern, setShowLoadPattern] = useState();
    const [spec, setSpec] = useState(null);
    const [draw, setDraw] = useState(1);
    const [dontCollapse, setDontCollapse] = useState(false);
    const redraw = () => setDraw(draw + 1);

    useEffect(() => {
        // retrieve spec if present
        httpAPI('', `${apiPaths.getLessonSpec}/${global.currentLessonObject._id}`, { method: 'GET' })
            .then(response => {
                const { lessonSpec } = response.data;
                setSpec(lessonSpec.specification);
            })
            .catch(e => {
                // missing, create one & save it
                const newSpec = {
                    group: '', title: '', level: '', tags: '', subtitle: '', listing: '', explanation: '',
                    sections: global.currentLessonObject.lessonNode.children.map((section, i) => ({
                        title: section.title,
                        index: i,
                        pages: section.children.map((page, j) => ({
                            title: page.title,
                            index: j,
                            type: page.componentName,
                            content: '',
                            elements: page.children[0].children.map(element => ({
                                type: element.componentName,
                                animation: elementComponents[element.componentName] === PatternAnimation && (
                                    computeAnimationLayout(element)[0].map(f => {
                                        return [f.label || f.name,
                                            `${f.sections.map(s => s.korean.text).join(' | ')}\n${f.sections.map(s => s.english.text).join(' | ')}`.trim(),
                                            f.parts.narration ? f.parts.narration.map((n, k) => `${k + 1}. ${n.value}`).join('\n') : '--bubbles--'];
                                    })),
                                collapsed: true,
                            })),
                        })),
                    })),
                };
                httpAPI('', `${apiPaths.postSpec}/${global.currentLessonObject._id}`, { data: { specification: newSpec } });
                setSpec(newSpec);
            });
    }, []);

    const saveSpec = (s) => httpAPI('', `${apiPaths.postSpec}/${global.currentLessonObject._id}`, { data: { specification: s || spec } });

    const updateSpec = () => { setSpec({ ...spec }); saveSpec(); };

    const onFieldBlur = (object, field, value) => {
        if (object[field] !== value) {
            object[field] = value;
            updateSpec(); // saveSpec();
        }
    };

    const onContextMenu = (e, context) => {
        e.preventDefault();
        e.stopPropagation();
        document.activeElement.blur();
        editor.update({ specContextMenu: { open: true, context, e } });
    };

    const LoadPatternDialog = () => {
        const [pattern, setPattern] = useState(null);
        const loadPattern = () => {
            console.log(pattern);
            const update = { ...spec,
                title: pattern.referenceDef.trim(),
                subtitle: pattern.shortDescr,
                listing: pattern.patterFunction ? `${pattern.patternType} acting as a ${pattern.patterFunction}` : pattern.patternType,
                level: pattern.gr.niklLevel,
                explanation: pattern.gr.defHTML.replace(/<[^>]*>/g, ''),
            };
            setSpec(update);
            saveSpec(update);
        };
        return (
            <PopupDialog
                open={showLoadPattern} setOpen={() => setShowLoadPattern(false)}
                title="Load details from a Grammar Library pattern" onOK={loadPattern} onCancel={() => {}}
            >
                <SelectGrammarPattern setPattern={setPattern} />
            </PopupDialog>
        );
    };

    const AnimCell = ({ object, animation, step, item }) => (
        <Cell>
            <ContentsText minRows={1} defaultValue={step[0]} spellCheck={false} onBlur={e => onFieldBlur(object, field, e.target.value)} />
        </Cell>
    );

    const AnimationSpec = ({ animPage, animation, animID }) => {

        const onColBlur = (e, step, col) => {
            if (step[col] !== e.target.value) {
                step[col] = e.target.value;
                updateSpec();
            }
        };

        return (
            <AnimSpecTable>
                <AnimSpecTableBody>
                    { animation.map((step, i) => (
                        <Field key={i} onContextMenu={e => onContextMenu(e, { animPage, animation, animID, step, stepIndex: i, mouseX: e.clientX, mouseY: e.clientY, e })}>
                            <Cell style={{ width: 100 }}>
                                <ContentsText defaultValue={step[0]} spellCheck={false} onBlur={e => onColBlur(e, step, 0)} />
                            </Cell>
                            <Cell style={{ width: 200 }}>
                                <ContentsText style={{ textAlign: 'center' }} defaultValue={step[1]} spellCheck={false} onBlur={e => onColBlur(e, step, 1)} />
                            </Cell>
                            <Cell>
                                <ContentsText defaultValue={step[2]} spellCheck={false} onBlur={e => onColBlur(e, step, 2)} />
                            </Cell>
                        </Field>
                    ))}
                </AnimSpecTableBody>
            </AnimSpecTable>
        );
    };

    const Contents = ({ object, field, minRows, contextMenu, colspan = 1 }) => {
        const animationElements = object.elements && object.elements.filter(e => elementComponents[e.type] === PatternAnimation && e.animation)
            .map(ae => { if (!ae.id) ae.id = Math.random(); return ae; });

        return (
            <ContentsCell colSpan={colspan} onContextMenu={contextMenu}>
                { animationElements && animationElements.length > 0 ? (
                    animationElements.map((ae, i) => (
                        <React.Fragment key={i}>
                            <Expander onClick={e => {
                                animExpanded[ae.id] = !animExpanded[ae.id]; setAnimExpanded({ ...animExpanded });
                            }}>{animExpanded[ae.id] ? '⊖' : '⊕'}
                            </Expander>
                            { animExpanded[ae.id] && <AnimationSpec key={i} animPage={object} animID={ae.id} animation={ae.animation} /> }
                        </React.Fragment>
                    ))
                ) : (
                    <ContentsText minRows={minRows || 1} defaultValue={object[field]} spellCheck={false} onBlur={e => onFieldBlur(object, field, e.target.value)} />
                )}
            </ContentsCell>
        );
    };

    const SectionPages = ({ section, page }) => (
        <>
            { page.index === 0 && (
                <Cell rowSpan={section.pages.length} onContextMenu={e => onContextMenu(e, { section, mouseX: e.clientX, mouseY: e.clientY, e })}>
                    <EditableLabel html={section.title} spellCheck={false} onBlur={e => onFieldBlur(section, 'title', e.target.textContent)} />
                </Cell>
            )}
            <Cell onContextMenu={e => onContextMenu(e, { page, section, mouseX: e.clientX, mouseY: e.clientY, e })}>
                <EditableLabel html={page.title} style={{ fontWeight: 500, maxWidth: 100 }} spellCheck={false} onBlur={e => onFieldBlur(page, 'title', e.target.textContent)} />
            </Cell>
            <Contents object={page} field="content" contextMenu={e => onContextMenu(e, { page, section, mouseX: e.clientX, mouseY: e.clientY, e })} />
        </>
    );

    return (
        <Draggable
            handle="#spec-box-handle" bounds={{ top: -300 }} axis="both" defaultPosition={{ x: 100, y: -250 }}>
            <LessonSpecBoxWrapper
                ref={specBoxRef} expanded={expanded} id="spec-box"
                onMouseEnter={() => setExpanded(true)} onMouseLeave={e => setExpanded(dontCollapse || e.clientX < specBoxRef.current.getBoundingClientRect().right)}
            >
                <EditorTitleBar id="spec-box-handle">
                    Lesson specification
                    <div><span onClick={e => editor.update({ lessonSpecBox: { show: false } })}></span></div>
                </EditorTitleBar>
                <ToolBar>
                    <SearchAndReplace srObject={srObject} setSRObject={setSRObject} spec={spec} updateSpec={updateSpec} onReplace={redraw} />
                    <SmallButton variant="outlined" onClick={() => setShowLoadPattern(true)}>Load pattern</SmallButton>
                    <CheckboxField label="Don't collapse" checked={dontCollapse} onChange={e => setDontCollapse(e.target.checked)} style={{ fontSize: 12, margin: '4px 0 0 10px' }} />
                    <div style={{ flexGrow: 1 }} />
                    <Hints>Cmd F = find/replace, Cmd S = save edits</Hints>
                </ToolBar>
                { spec && (
                    <SpecTable>
                        <SpecTableBody>
                            <Field><Label>Title</Label><Contents colspan={2} object={spec} field="title" /></Field>
                            <Field><Label>Subtitle</Label><Contents colspan={2} object={spec} field="subtitle" /></Field>
                            <Field><Label>Listing</Label><Contents colspan={2} object={spec} field="listing" /></Field>
                            <Field><Label>Group</Label><Cell colSpan={2}><Select selection={spec.group} options={groupChoices} onChange={e => onFieldBlur(spec, 'group', e.target.value)} /></Cell></Field>
                            <Field><Label>Level</Label><Contents colspan={2} object={spec} field="level" /></Field>
                            <Field><Label>Tags</Label><Contents colspan={2} object={spec} field="tags" /></Field>
                            <Field><Label>Explanation</Label><Contents colspan={2} object={spec} field="explanation" minRows={3} /></Field>
                            <Field style={{ backgroundColor: '#f2f2f2' }}><Label>Sections</Label><Label>Pages</Label><Label style={{ width: '100%' }}>Content</Label></Field>
                            { spec.sections.map(section => section.pages.map(page => (
                                <Field key={`${section.index}-${page.index}`}><SectionPages section={section} page={page} /></Field>
                            )))}
                        </SpecTableBody>
                    </SpecTable>
                )}
                { showLoadPattern && <LoadPatternDialog /> }
                { editor.specContextMenu.open && (
                    <SpecContextMenu
                        spec={spec} ref={menuRef} updateSpec={updateSpec} setSRObject={setSRObject} onClose={() => {
                            if (document.activeElement) document.activeElement.blur();
                        }} />
                ) }
            </LessonSpecBoxWrapper>
        </Draggable>
    );
};

export default LessonSpecBox;
